import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Space, Typography, Button, Row, Col, Statistic, Alert, Select, Tooltip, Drawer, message } from 'antd';
import { ReloadOutlined, ThunderboltOutlined, WarningOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, TeamOutlined, CopyOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_META = {
  success: { color: 'green', label: 'Failover OK', icon: <CheckCircleOutlined /> },
  no_alternative: { color: 'red', label: 'Sem alternativa', icon: <WarningOutlined /> },
  error: { color: 'volcano', label: 'Erro', icon: <CloseCircleOutlined /> },
  throttled_repeat: { color: 'gold', label: 'Repetido (throttled)', icon: <ClockCircleOutlined /> },
  no_customer_match: { color: 'default', label: 'Sem customer match', icon: null },
  disabled: { color: 'default', label: 'Feature off', icon: null },
};

function StateBadge({ state }) {
  if (state === 'open') return <Tag color="green" icon={<CheckCircleOutlined />}>open</Tag>;
  if (state === 'close') return <Tag color="red" icon={<CloseCircleOutlined />}>close</Tag>;
  if (state === 'connecting') return <Tag color="gold" icon={<ClockCircleOutlined />}>connecting</Tag>;
  return <Tag color="default">unknown</Tag>;
}

const ChipFailures = () => {
  const [health, setHealth] = useState(null);
  const [failures, setFailures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingHealth, setRefreshingHealth] = useState(false);
  const [filters, setFilters] = useState({ days: 7, instance: null, status: null });
  const [teamsDrawer, setTeamsDrawer] = useState({ open: false, instance: null, data: null, loading: false });

  const openTeamsDrawer = useCallback(async (instance) => {
    setTeamsDrawer({ open: true, instance, data: null, loading: true });
    try {
      const res = await api.get(`/monitoring/chip-teams?instance=${encodeURIComponent(instance)}`);
      setTeamsDrawer({ open: true, instance, data: res.data, loading: false });
    } catch (err) {
      console.error('erro chip-teams:', err);
      message.error('Erro ao carregar times do chip');
      setTeamsDrawer({ open: true, instance, data: null, loading: false });
    }
  }, []);

  const copyTeamsList = useCallback((teams) => {
    const lines = teams.map((t) => {
      const backup = t.backup_count === 0
        ? 'SEM BACKUP'
        : `backup: ${t.backup_chips.map((b) => b.instance_name).join(', ')}`;
      const phone = t.cellphone_number ? ` — ${t.cellphone_number}` : '';
      return `${t.game_name}${phone} — ${backup}`;
    });
    const text = lines.join('\n');
    navigator.clipboard.writeText(text)
      .then(() => message.success(`${teams.length} times copiados`))
      .catch(() => message.error('Erro ao copiar'));
  }, []);

  const fetchHealth = useCallback(async () => {
    setRefreshingHealth(true);
    try {
      const res = await api.get('/monitoring/chip-health');
      setHealth(res.data);
    } catch (err) {
      console.error('erro chip-health:', err);
    }
    setRefreshingHealth(false);
  }, []);

  const fetchFailures = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('days', String(filters.days));
      if (filters.instance) params.set('instance', filters.instance);
      if (filters.status) params.set('status', filters.status);
      const res = await api.get(`/monitoring/chip-failures?${params.toString()}`);
      setFailures(res.data);
    } catch (err) {
      console.error('erro chip-failures:', err);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);
  useEffect(() => { fetchFailures(); }, [fetchFailures]);

  const chips = health?.chips || [];
  const totalActiveTeams = chips.reduce((sum, c) => sum + (c.active_with_open_game || 0), 0);
  const totalFailures24h = chips.reduce((sum, c) => sum + (c.failures_24h?.total || 0), 0);
  const totalMsgs24h = chips.reduce((sum, c) => sum + (c.msgs_24h || 0), 0);
  const chipsWithProblems = chips.filter((c) => c.connection_state !== 'open').length;

  const eventsColumns = [
    {
      title: 'Quando',
      dataIndex: 'created_at',
      width: 160,
      render: (v) => (
        <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm:ss')}>
          {dayjs(v).fromNow()}
        </Tooltip>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'customer_name',
      render: (v, row) => v ? (
        <a href={`/customers/${row.customer_id}/ficha`}>{v}</a>
      ) : <Text type="secondary">#{row.customer_id ?? '—'}</Text>,
    },
    {
      title: 'Chip origem',
      dataIndex: 'from_instance',
      width: 140,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Failover pra',
      dataIndex: 'to_instance',
      width: 140,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 160,
      render: (v) => {
        const m = STATUS_META[v] || { color: 'default', label: v };
        return <Tag color={m.color} icon={m.icon}>{m.label}</Tag>;
      },
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      ellipsis: true,
    },
    {
      title: 'Tentativas',
      dataIndex: 'tested_instances',
      width: 200,
      render: (v) => v && v.length ? (
        <Tooltip title={v.join(', ')}>
          <Text type="secondary">{v.length} probes</Text>
        </Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
  ];

  const chipCards = chips.map((c) => {
    const isProblem = c.connection_state !== 'open';
    const isHot = (c.failures_24h?.total || 0) > 5;
    return (
      <Col key={c.name} xs={24} sm={12} md={8} lg={8}>
        <Card
          title={
            <Space>
              <ThunderboltOutlined style={{ color: isProblem ? '#ff4d4f' : '#52c41a' }} />
              <span>{c.name}</span>
              <StateBadge state={c.connection_state} />
            </Space>
          }
          size="small"
          extra={<Tag color={c.db_status === 'ATIVO' ? 'blue' : c.db_status === 'LIMITE_ATINGIDO' ? 'gold' : 'default'}>{c.db_status || 'sem status DB'}</Tag>}
          style={isProblem ? { borderColor: '#ff4d4f' } : isHot ? { borderColor: '#faad14' } : undefined}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Statistic title="Times ativos" value={c.active_with_open_game} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {c.active_with_group} c/ grupo · {c.active_with_open_game} c/ jogo
              </Text>
            </Col>
            <Col span={8}>
              <Statistic
                title="Msgs 24h"
                value={c.msgs_24h || 0}
                valueStyle={{ color: (c.msgs_failed_24h || 0) > 0 ? '#faad14' : undefined }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {c.msgs_7d ? `${c.msgs_7d} em 7d` : '—'}
                {(c.msgs_failed_24h || 0) > 0 && ` · ${c.msgs_failed_24h} falhou`}
              </Text>
            </Col>
            <Col span={8}>
              <Statistic title="Falhas 24h" value={c.failures_24h?.total || 0} valueStyle={{ color: (c.failures_24h?.total || 0) > 0 ? '#faad14' : undefined }} />
              {c.last_failure_at && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  última: {dayjs(c.last_failure_at).fromNow()}
                </Text>
              )}
            </Col>
          </Row>
          {(c.failures_24h?.total || 0) > 0 && (
            <div style={{ marginTop: 8 }}>
              <Space size={4} wrap>
                {c.failures_24h.success > 0 && <Tag color="green">✓ {c.failures_24h.success} failover</Tag>}
                {c.failures_24h.no_alternative > 0 && <Tag color="red">⚠ {c.failures_24h.no_alternative} sem alt</Tag>}
                {c.failures_24h.error > 0 && <Tag color="volcano">✗ {c.failures_24h.error} erro</Tag>}
                {c.failures_24h.throttled > 0 && <Tag color="gold">⏱ {c.failures_24h.throttled} throttle</Tag>}
              </Space>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <Button size="small" icon={<TeamOutlined />} onClick={() => openTeamsDrawer(c.name)} disabled={c.active_with_open_game === 0} block>
              Ver times ({c.active_with_open_game})
            </Button>
          </div>
        </Card>
      </Col>
    );
  });

  const chipOptions = chips.map((c) => (
    <Option key={c.name} value={c.name}>{c.name}</Option>
  ));

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <ThunderboltOutlined /> Saúde dos Chips
          </Title>
          <Text type="secondary">
            connectionState em tempo real (Evolution) + failover events do banco
          </Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchHealth(); fetchFailures(); }} loading={refreshingHealth || loading}>
            Atualizar
          </Button>
        </Col>
      </Row>

      {chipsWithProblems > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${chipsWithProblems} chip(s) fora do ar (state ≠ open)`}
          description="Chips com state=close podem estar aguardando reconexão ou terem sido de-registered do WhatsApp Web."
        />
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Times ativos c/ jogo" value={totalActiveTeams} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Msgs enviadas 24h" value={totalMsgs24h} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Falhas nas últimas 24h" value={totalFailures24h} valueStyle={{ color: totalFailures24h > 20 ? '#ff4d4f' : totalFailures24h > 5 ? '#faad14' : undefined }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="Chips saudáveis" value={`${chips.length - chipsWithProblems}/${chips.length}`} /></Card>
        </Col>
      </Row>

      <Title level={4}>Chips</Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {chipCards}
      </Row>

      <Title level={4}>Histórico de failover events</Title>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>Janela:</span>
          <Select value={filters.days} onChange={(v) => setFilters((f) => ({ ...f, days: v }))} style={{ width: 120 }}>
            <Option value={1}>24h</Option>
            <Option value={7}>7 dias</Option>
            <Option value={30}>30 dias</Option>
          </Select>
          <span>Chip:</span>
          <Select allowClear placeholder="todos" value={filters.instance} onChange={(v) => setFilters((f) => ({ ...f, instance: v || null }))} style={{ width: 180 }}>
            {chipOptions}
          </Select>
          <span>Status:</span>
          <Select allowClear placeholder="todos" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v || null }))} style={{ width: 200 }}>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <Option key={k} value={k}>{m.label}</Option>
            ))}
          </Select>
        </Space>
      </Card>

      {failures?.summary_by_group?.length > 0 && (
        <Card size="small" title="Grupos mais afetados no período" style={{ marginBottom: 16 }}>
          <Table
            size="small"
            pagination={false}
            dataSource={failures.summary_by_group}
            rowKey={(r) => `${r.group_jid}-${r.from_instance}`}
            columns={[
              {
                title: 'Time',
                dataIndex: 'customer_name',
                render: (v, row) => v ? <a href={`/customers/${row.customer_id}/ficha`}>{v}</a> : <Text type="secondary">#{row.customer_id ?? '—'}</Text>,
              },
              { title: 'Grupo (JID)', dataIndex: 'group_jid', render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
              { title: 'Chip', dataIndex: 'from_instance', render: (v) => <Tag>{v}</Tag> },
              { title: 'Eventos', dataIndex: 'count', width: 100 },
            ]}
          />
        </Card>
      )}

      <Table
        size="small"
        loading={loading}
        dataSource={failures?.events || []}
        columns={eventsColumns}
        rowKey="id"
        pagination={{ pageSize: 25, showSizeChanger: true }}
      />

      <Drawer
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Times ativos em <Text code>{teamsDrawer.instance}</Text></span>
          </Space>
        }
        placement="right"
        width={900}
        onClose={() => setTeamsDrawer({ open: false, instance: null, data: null, loading: false })}
        open={teamsDrawer.open}
        extra={
          teamsDrawer.data?.teams?.length > 0 && (
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyTeamsList(teamsDrawer.data.teams.filter((t) => t.backup_count === 0))}
                disabled={teamsDrawer.data.summary.without_backup === 0}
              >
                Copiar só sem backup ({teamsDrawer.data.summary.without_backup})
              </Button>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => copyTeamsList(teamsDrawer.data.teams)}
              >
                Copiar todos ({teamsDrawer.data.summary.total})
              </Button>
            </Space>
          )
        }
      >
        {teamsDrawer.loading && <div>Carregando...</div>}
        {teamsDrawer.data && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic title="Total de times" value={teamsDrawer.data.summary.total} />
              </Col>
              <Col span={8}>
                <Statistic title="Com chip backup" value={teamsDrawer.data.summary.with_backup} valueStyle={{ color: '#52c41a' }} />
              </Col>
              <Col span={8}>
                <Statistic title="SEM backup" value={teamsDrawer.data.summary.without_backup} valueStyle={{ color: teamsDrawer.data.summary.without_backup > 0 ? '#ff4d4f' : undefined }} />
              </Col>
            </Row>

            {teamsDrawer.data.summary.without_backup > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Times sem chip backup travam se este chip cair"
                description="Contate o organizador pra adicionar outros números (instance-no-grupo-*) como membros do grupo. Sem backup, o failover automático não funciona."
              />
            )}

            <Table
              size="small"
              dataSource={teamsDrawer.data.teams}
              rowKey="id"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              rowClassName={(row) => row.backup_count === 0 ? 'row-warning' : ''}
              columns={[
                {
                  title: 'Time',
                  dataIndex: 'game_name',
                  render: (v, row) => (
                    <a href={`/customers/${row.id}/ficha`}>{v}</a>
                  ),
                },
                {
                  title: 'WhatsApp',
                  dataIndex: 'cellphone_number',
                  width: 140,
                  render: (v) => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'Próximo jogo',
                  dataIndex: 'next_game_at',
                  width: 140,
                  render: (v) => v ? (
                    <Tooltip title={dayjs(v).format('DD/MM/YYYY HH:mm')}>
                      {dayjs(v).fromNow()}
                    </Tooltip>
                  ) : <Text type="secondary">—</Text>,
                },
                {
                  title: 'Backup chips',
                  dataIndex: 'backup_chips',
                  render: (chips) => {
                    if (!chips || chips.length === 0) {
                      return <Tag color="red" icon={<WarningOutlined />}>SEM BACKUP</Tag>;
                    }
                    return (
                      <Space size={4} wrap>
                        {chips.map((b) => (
                          <Tooltip key={b.instance_name} title={`validado ${dayjs(b.last_validated_at).fromNow()}`}>
                            <Tag color="blue">{b.instance_name}</Tag>
                          </Tooltip>
                        ))}
                      </Space>
                    );
                  },
                },
              ]}
            />
            <style>{`.row-warning td { background-color: #fff2f0 !important; }`}</style>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ChipFailures;
